import React from "react";
import Typography from "@mui/material/Typography";
import snakeCase from "lodash/snakeCase";
import { formatAssetDimensions } from "../../../utils/dimensions";

const styles = {
  label: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

const isValidSerialNumber = value => value && value.toLowerCase() !== "n/a";

const hasValue = value => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

const getCustomFieldValue = (asset, selectedField) => {
  const selectedFieldSnakeCase = snakeCase(selectedField);
  const customFieldMatch =
    selectedField.match(/^customField(\d+)$/) ||
    selectedFieldSnakeCase.match(/^custom_field_(\d+)$/);

  if (!customFieldMatch) return null;

  const templateId = customFieldMatch[1];
  const customField = asset.customFields?.find(
    cf => String(cf.customFieldTemplate?.id) === templateId
  );

  return hasValue(customField?.value) ? customField.value : null;
};

const getFieldValue = (asset, selectedField) => {
  if (selectedField === "itemId") {
    const serialNumber = asset?.serialNumber || asset?.serial_number;
    const barcode = asset?.barcode;

    if (isValidSerialNumber(serialNumber)) {
      return `${asset.itemId}, S/N: ${serialNumber}`;
    }

    if (barcode) {
      return `${asset.itemId}, ${barcode}`;
    }

    return asset.itemId;
  }

  if (selectedField === "dimensions") {
    return formatAssetDimensions(asset[selectedField]);
  }

  if (selectedField === "rackLocations" && Array.isArray(asset[selectedField])) {
    return asset[selectedField]
      .flatMap(rl => (rl.warehouse_locations || []).map(loc => loc.location_name))
      .filter(Boolean)
      .join(", ") || "N/A";
  }

  const directValue = asset[selectedField];
  if (hasValue(directValue)) return directValue;

  const snakeCaseValue = asset[snakeCase(selectedField)];
  if (hasValue(snakeCaseValue)) return snakeCaseValue;

  const customFieldValue = getCustomFieldValue(asset, selectedField);
  if (hasValue(customFieldValue)) return customFieldValue;

  return "N/A";
};

export default function AssetItemLabel({ asset, selectedField, suffix }) {
  if (!selectedField) return null;
  const value = getFieldValue(asset, selectedField);

  return (
    <Typography color="primary" lineHeight={1.2} fontWeight={500} variant="body2" sx={styles.label}>
      {value}
      {suffix || ""}
    </Typography>
  );
}
